import React from 'react'
import { styled } from 'baseui'
import { lightTheme } from '@/customTheme'
import Icons from '../../../Icons'

const BACKGROUND_KINDS = {
  white: {
    default: lightTheme.colors.white,
    hover:   lightTheme.colors.grayScale50,
    active:  lightTheme.colors.grayScale100,
  },
  gray: {
    default: lightTheme.colors.grayScale50,
    hover:   lightTheme.colors.grayScale100,
    active:  lightTheme.colors.grayScale150,
  },
} as const

type BackgroundKind = keyof typeof BACKGROUND_KINDS

// Variants — each entry is the single source of truth for a button style.
// Add a new entry here to introduce a new style; no other changes required.
export type ButtonVariant = 'menu' | 'menu-danger' | 'suggest'

export const BUTTON_VARIANTS: Record<ButtonVariant, {
  backgroundKind: BackgroundKind
  typography:     React.CSSProperties
  normalColor:    string
  iconSize:       string
  minHeight:      string
  truncate:       boolean
}> = {
  // White bg · 14px/500 · blackGray (#111) — list item with icon (resize, suggestions with icon)
  menu: {
    backgroundKind: 'white',
    typography:     lightTheme.typography.Small14regular,
    normalColor:    lightTheme.colors.blackGray,
    iconSize:       '24px',
    minHeight:      '40px',
    truncate:       false,
  },
  // White bg · 14px/500 · red — destructive action (logo delete)
  'menu-danger': {
    backgroundKind: 'white',
    typography:     lightTheme.typography.Small14regular,
    normalColor:    lightTheme.colors.text.text_warning,
    iconSize:       '24px',
    minHeight:      '40px',
    truncate:       false,
  },
  // Gray bg · 12px/400 · blackGray · truncated — suggest/pose items
  suggest: {
    backgroundKind: 'gray',
    typography:     lightTheme.typography.Small12regular,
    normalColor:    lightTheme.colors.blackGray,
    iconSize:       '22px',
    minHeight:      '40px',
    truncate:       true,
  },
}

const VerticalListButtonShell = styled('div', ({ $backgroundKind, $disabled }: { $backgroundKind: BackgroundKind; $disabled?: boolean }) => {
  const bg = BACKGROUND_KINDS[$backgroundKind]
  return {
    borderRadius:  '8px',
    padding:       '8px',
    gap:           '8px',
    display:       'flex',
    alignItems:    'center',
    textAlign:     'left' as const,
    pointerEvents: $disabled ? 'none' : 'auto',
    background:    bg.default,
    ':hover':  { background: !$disabled ? bg.hover : undefined, cursor: !$disabled ? 'pointer' : undefined },
    ':active': { background: !$disabled ? bg.active : undefined },
  }
})

export interface VerticalListButtonProps {
  variant?:      ButtonVariant
  text?:          string
  icon?:         React.ReactNode
  rightElement?: React.ReactNode
  disabled?:     boolean
  onClick?:      () => void
  /** When true and no rightElement is provided, displays a checkmark on the right */
  selected?:     boolean
}

export const VerticalListButton: React.FC<VerticalListButtonProps> = ({
  variant = 'menu',
  text,
  icon,
  rightElement,
  disabled,
  onClick,
  selected,
}) => {
  const { backgroundKind, typography, normalColor, iconSize, minHeight, truncate } = BUTTON_VARIANTS[variant]
  const color = disabled ? lightTheme.colors.text.text_200_disable : normalColor
  const resolvedRightElement = rightElement ?? (selected ? <Icons.CheckIcon fill={lightTheme.colors.primary} /> : undefined)
  
  return (
    <VerticalListButtonShell $backgroundKind={backgroundKind} $disabled={disabled} onClick={onClick} style={{ minHeight }}>
      {icon && (
        <div style={{ flexShrink: 0, width: iconSize, height: iconSize, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </div>
      )}
      <span style={{ flex: resolvedRightElement ? '1 0 auto' : '1 1 0%', minWidth: 0, ...typography, color, ...(truncate && !resolvedRightElement ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}) }}>
        {text}
      </span>
      {resolvedRightElement}
    </VerticalListButtonShell>
  )
}